import { useEffect, useRef, type RefObject } from 'react'

/**
 * Maakt een paneel echt modaal, net als de Drawer in de shop: de pagina erachter scrollt niet en
 * is onbereikbaar (inert), Tab blijft in het paneel, Escape sluit, en de focus gaat daarna terug
 * naar de knop die het opende.
 */
export function useModalLock(open: boolean, panel: RefObject<HTMLElement | null>, page: RefObject<HTMLElement | null>, onClose: () => void) {
  // Via een ref: een nieuwe onClose per render mag het slot niet opnieuw zetten (dan springt de focus).
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    if (!open) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const behind = page.current
    behind?.setAttribute('inert', '')
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close.current()
        return
      }
      if (e.key !== 'Tab' || !panel.current) return
      const focusable = panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = overflow
      behind?.removeAttribute('inert')
      window.removeEventListener('keydown', onKey)
      // Na navigeren is de knop er nog steeds (tabbalk); focus terug, zonder te scrollen.
      if (opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open, panel, page])
}
